import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent, getAttribution } from "@/lib/analytics";

const WINDOW_COUNT = ["Just a few (1–5)", "One area / room (6–12)", "Whole home (13–30)", "Large home (30+)", "Not sure"];
const GOALS = ["Lower cooling bills", "Block UV / stop furniture fading", "More privacy", "Reduce glare", "All of the above"];

/**
 * Self-contained residential tint 3-step quote form (goal → budget ack → contact).
 * Used by ResidentialTintLanding (card styling) and the /mockup wizard tint branch (bare).
 * No Toaster here — host page provides it. `bare` drops the outer card chrome for embedding.
 */
export default function ResidentialTintForm({ source, lpKey, testidPrefix, bare = false, projectType = "Residential Window Tint" }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", street_address: "",
    window_count: "", goal: "", budget_ack: "",
  });
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const STEPS = ["Your project", "Pricing", "Contact"];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const canSubmit =
    form.full_name.trim() && form.email.trim() && form.phone.trim() &&
    form.window_count && form.goal && form.budget_ack;

  const step0Ready = form.window_count && form.goal;
  const step1Ready = Boolean(form.budget_ack);
  const step2Ready = form.full_name.trim() && form.email.trim() && form.phone.trim();

  const next = () => {
    const nextStep = step + 1;
    trackEvent("lp_form_step_view", { lp: lpKey, step: nextStep + 1, step_name: STEPS[nextStep] });
    setStep(nextStep);
  };
  const back = () => setStep(Math.max(0, step - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      const details = [
        form.street_address && `City / Location\n  → ${form.street_address}`,
        `Roughly how many windows need film?\n  → ${form.window_count}`,
        `#1 Goal\n  → ${form.goal}`,
        `Budget acknowledgment\n  → ${form.budget_ack}`,
      ].filter(Boolean).join("\n\n");

      await api.post("/leads", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        project_type: projectType,
        project_details: details,
        referral_source: source,
        ...getAttribution(),
      });
      toast.success("Quote request received!");
      trackEvent("lp_form_submit", { lp: lpKey });
      const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      navigate("/mockup/thank-you", {
        state: {
          fromSubmit: true,
          submissionId,
          service: projectType,
          vehicle: "n/a",
          has_business: false,
          source,
        },
        replace: true,
      });
    } catch (err) {
      trackEvent("lp_form_error", { lp: lpKey, message: formatApiErrorDetail(err.response?.data?.detail) });
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      className={bare ? "" : "bg-white rounded-3xl border border-neutral-200 p-6 md:p-8 shadow-xl"}
    >
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1 flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold border transition-colors ${i <= step ? "bg-[var(--red)] text-white border-[var(--red)]" : "bg-white text-neutral-500 border-neutral-200"}`}>
              {i < step ? <Check size={12}/> : i + 1}
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-[2px] bg-neutral-200 overflow-hidden">
                <div className="h-full bg-[var(--red)] transition-all duration-300" style={{ width: i < step ? "100%" : "0" }}/>
              </div>
            )}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* STEP 1 — Goal + Window Count (low-friction taps, no typing) */}
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 1 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">What&apos;s your #1 goal?</h3>

            <div className="mt-5 space-y-2">
              {GOALS.map((g) => {
                const active = form.goal === g;
                return (
                  <button
                    key={g}
                    type="button"
                    data-testid={`${testidPrefix}-goal-${active ? "active" : "opt"}-${g.slice(0,8).replace(/[^a-z]/gi,"").toLowerCase()}`}
                    onClick={() => setForm({ ...form, goal: g })}
                    className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${active ? "border-[var(--red)] bg-red-50/60" : "border-neutral-200 hover:border-neutral-400"}`}
                  >
                    <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center ${active ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    <span className={`text-sm ${active ? "text-neutral-900 font-semibold" : "text-neutral-700"}`}>{g}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <label className="eyebrow">Roughly how many windows?</label>
              <select data-testid={`${testidPrefix}-window-count`} required className="input-928 mt-2" value={form.window_count} onChange={set("window_count")}>
                <option value="">Select…</option>
                {WINDOW_COUNT.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>

            <button
              type="button"
              data-testid={`${testidPrefix}-next-1`}
              disabled={!step0Ready}
              onClick={next}
              className={`btn-red w-full justify-center mt-6 ${!step0Ready ? "opacity-60 pointer-events-none" : ""}`}
            >
              Continue <ArrowRight size={16}/>
            </button>
          </motion.div>
        )}

        {/* STEP 2 — Budget acknowledgment */}
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 2 of 3</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">A quick heads-up on pricing.</h3>

            <div data-testid={`${testidPrefix}-budget-ack`} className="mt-5 p-5 rounded-2xl border border-neutral-200">
              <div className="font-display text-lg text-neutral-900 leading-snug">
                Residential tinting typically starts around $500+
              </div>
              <p className="mt-2 text-sm text-neutral-500 leading-relaxed">
                Final pricing depends on window count, film type, and access. Do you understand the starting budget?
              </p>
              <div className="mt-4 space-y-2.5">
                {[
                  { value: "Yes, I understand pricing starts at $500+", label: "Yes, I understand pricing starts at $500+" },
                  { value: "No, I need more pricing explanation", label: "No, I need more pricing explanation" },
                ].map((opt) => {
                  const active = form.budget_ack === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`${testidPrefix}-budget-ack-${active ? "active" : "opt"}-${opt.value.startsWith("Yes") ? "yes" : "no"}`}
                      onClick={() => setForm({ ...form, budget_ack: opt.value })}
                      className={`w-full text-left flex items-start gap-3 p-3 rounded-xl transition-all ${active ? "bg-red-50/60" : "hover:bg-neutral-50"}`}
                    >
                      <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 grid place-items-center transition-colors ${active ? "border-[var(--red)] bg-[var(--red)]" : "border-neutral-300"}`}>
                        {active && <span className="w-2 h-2 rounded-full bg-white" />}
                      </span>
                      <span className={`text-sm ${active ? "text-neutral-900 font-semibold" : "text-neutral-700"}`}>{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid={`${testidPrefix}-back-1`} onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="button"
                data-testid={`${testidPrefix}-next-2`}
                disabled={!step1Ready}
                onClick={next}
                className={`btn-red ${!step1Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                Continue <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — Contact (the "closer") */}
        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
            <div className="eyebrow eyebrow-red">Step 3 of 3 · Last step</div>
            <h3 className="font-display text-2xl mt-2 leading-tight">Where should Dave send it?</h3>
            <p className="text-sm text-neutral-500 mt-1">He&apos;ll call within 1 business day.</p>

            <div className="mt-5 space-y-3">
              <input data-testid={`${testidPrefix}-name`} required placeholder="Full name *" className="input-928" value={form.full_name} onChange={set("full_name")} />
              <input data-testid={`${testidPrefix}-email`} required type="email" placeholder="Email *" className="input-928" value={form.email} onChange={set("email")} />
              <input data-testid={`${testidPrefix}-phone-input`} required type="tel" placeholder="Phone *" className="input-928" value={form.phone} onChange={set("phone")} />
              <input data-testid={`${testidPrefix}-address`} placeholder="City / Location (e.g. Prescott, Chino, Williams)" className="input-928" value={form.street_address} onChange={set("street_address")} />
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button type="button" data-testid={`${testidPrefix}-back-2`} onClick={back} className="btn-ghost"><ArrowLeft size={16}/> Back</button>
              <button
                type="submit"
                data-testid={`${testidPrefix}-submit`}
                disabled={loading || !canSubmit || !step2Ready}
                className={`btn-red flex-1 justify-center whitespace-nowrap ${loading || !canSubmit || !step2Ready ? "opacity-60 pointer-events-none" : ""}`}
              >
                {loading ? "Sending…" : "Send my request"}
                <ArrowRight size={16}/>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-6 text-xs text-neutral-500 text-center">We never share your info. Ever. · Dave replies within 1 business day.</p>
    </form>
  );
}
